import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cfpBaseUrl } from '../../network/urls';
import { resolveScopedTableOfficeCode, resolveTableApiOfficeLevel } from '../../utils/adminCompany';


/** -1 = Descending, 1 = Ascending */
export const DASH_SORT_ORDER_DESC = -1;
export const DASH_SORT_ORDER_ASC = 1;

const initialState = {
    data: [],
    currentPage: 1,
    pageSize: 20,
    dateFrom: '',
    dateTo: '',
    loadFrom: '',
    loadTo: '',
    applicationProgressId: '',
    objectionStatus: '',
    sortOrder: DASH_SORT_ORDER_DESC,
    totalPages: null,
    totalRecords: null,
    isLoading: false,
    error: null,
    // Only the latest fetch may write data (see extraReducers)
    latestRequestId: null,
}

// Define an async thunk for fetching paginated data
export const fetchPaginatedReconnectionNormal = createAsyncThunk(
    'generalServices/fetchReconnectionNormal',
    async ({ page, pageSize, dateFrom, dateTo, loadFrom, loadTo, applicationProgressId, objectionStatus,officeCode, sortOrder }, { getState }) => {
        const { auth } = getState();
        const { token, code, hierarchy, cfcLevelId, showAllApplicationsFlag, roleId, userId, selectedAdminCompanyCode } = auth;
        if (token) {
            const myHeaders = new Headers();
            myHeaders.append("Authorization", token);

            const formdata = new FormData();
            const resolvedSortOrder =
                Number(sortOrder) === DASH_SORT_ORDER_ASC ? DASH_SORT_ORDER_ASC : DASH_SORT_ORDER_DESC;
            formdata.append("pageNumber", page);
            formdata.append("pageSize", pageSize);
            const effectiveOfficeCode = resolveScopedTableOfficeCode(code, roleId, officeCode, selectedAdminCompanyCode);
            const effectiveOfficeLevel = resolveTableApiOfficeLevel({
                userCode: code,
                effectiveOfficeCode,
                hierarchy,
                cfcLevelId,
            });
            formdata.append("officeCode", effectiveOfficeCode);
            formdata.append("officeLevel", effectiveOfficeLevel);
            formdata.append("sortOrder", String(resolvedSortOrder));
            formdata.append("showAllApplicationFlag", showAllApplicationsFlag);
            formdata.append("overDueFlag", 0);
            formdata.append("dateFrom", dateFrom);
            formdata.append("dateTo", dateTo);
            formdata.append("loadFrom", loadFrom);
            formdata.append("loadTo", loadTo);
            formdata.append("applicationProgressId", applicationProgressId);
            formdata.append("objectionStatus", objectionStatus ? objectionStatus : 0);
            formdata.append("userId", roleId == 113 || roleId == 1146 ? userId : ''); 

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                body: formdata,
                redirect: "follow"
            };

            const response = await fetch(cfpBaseUrl + `comman/getAllRCOApplications`, requestOptions);
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Stats API issue!');
            }

            const result = await response.json();

            if (result.status !== "200" && result.status !== "400") {
                throw new Error(result.message, 'printing error message' || 'Unable to fetch stats!');
            }

            return result;
        }
    }
);

const reconnectionNormalPaginatedSlice = createSlice({
    name: 'reconnecionNormalPaginated',
    initialState: initialState,
    reducers: {
        nextPage(state) {
            state.currentPage += 1;
        },
        previousPage(state) {
            if (state.currentPage > 1) {
                state.currentPage -= 1;
            }
        },
        filterProgressId(state, action) {
            state.applicationProgressId = action.payload
        },
        resetProgressIdFilter(state) {
            state.applicationProgressId = ''
        },
        filterObjectionStatus(state, action) {
            state.objectionStatus = action.payload
        },
        resetObjectionStatusFilter(state) {
            state.applicationProgressId = ''
        },
        filterDateFrom(state, action) {
            state.dateFrom = action.payload
        },
        filterDateTo(state, action) {
            state.dateTo = action.payload
        },
        resetDateFilter(state) {
            state.dateFrom = ''
            state.dateTo = ''
        },
        filterLoadFrom(state, action) {
            state.loadFrom = action.payload
        },
        filterLoadTo(state, action) {
            state.loadTo = action.payload
        },
        resetLoadFilter(state) {
            state.loadFrom = ''
            state.loadTo = ''
        },
        filterSortOrder(state, action) {
            const next = Number(action.payload);
            state.sortOrder =
                next === DASH_SORT_ORDER_ASC ? DASH_SORT_ORDER_ASC : DASH_SORT_ORDER_DESC;
        },
        resetSortOrder(state) {
            state.sortOrder = DASH_SORT_ORDER_DESC;
        },
        firstPage(state) {
            state.currentPage = 1;
        },
        lastPage(state, action) {
            state.currentPage = action.payload;
        },
        // Clears every table filter (used when leaving the page). Back to page 1 only if a filter
        // was set, since the page number belonged to the filtered list.
        resetFilters(state) {
            const isSet = (value) => value !== '' && value != null
            const hadFilter = [state.dateFrom, state.dateTo, state.loadFrom, state.loadTo, state.applicationProgressId, state.objectionStatus].some(isSet) ||
                Number(state.sortOrder) === DASH_SORT_ORDER_ASC
            state.dateFrom = ''
            state.dateTo = ''
            state.loadFrom = ''
            state.loadTo = ''
            state.applicationProgressId = ''
            state.objectionStatus = ''
            state.sortOrder = DASH_SORT_ORDER_DESC
            if (hadFilter) state.currentPage = 1
        },

        resetState(state) {
            state.latestRequestId = null
            state.data = []
            state.currentPage = 1
            state.pageSize = 20
            state.dateFrom = ''
            state.dateTo = ''
            state.loadFrom = ''
            state.loadTo = ''
            state.applicationProgressId = ''
            state.objectionStatus = ''
            state.totalPages = null
            state.totalRecords = null
            state.isLoading = false
            state.error = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPaginatedReconnectionNormal.pending, (state, action) => {
                state.latestRequestId = action.meta.requestId;
                state.isLoading = true;
            })
            .addCase(fetchPaginatedReconnectionNormal.fulfilled, (state, action) => {
                // An older request finishing late (e.g. one with a filter that has since been
                // changed or cleared) must not overwrite the newer list
                if (action.meta.requestId !== state.latestRequestId) return;
                state.isLoading = false;
                if (action.payload.data) {
                    state.data = action.payload.data;
                } else {
                    state.data = [];
                }
                if (action.payload.totalPages) {
                    state.totalPages = action.payload.totalPages;
                } else {
                    state.totalPages = 1;
                }
                state.totalRecords = action.payload.totalRecords;
                state.error = null;
            })
            .addCase(fetchPaginatedReconnectionNormal.rejected, (state, action) => {
                if (action.meta.requestId !== state.latestRequestId) return;
                state.isLoading = false;
                state.error = action.error.message;
                state.totalPages = null;
                state.totalRecords = null;
            });
    },
});

export const { nextPage, previousPage, firstPage, lastPage, filterProgressId, filterObjectionStatus, filterDateFrom, filterDateTo, resetProgressIdFilter, resetObjectionStatusFilter, resetDateFilter, resetState, filterLoadFrom, filterLoadTo, resetLoadFilter, resetFilters , filterSortOrder, resetSortOrder} = reconnectionNormalPaginatedSlice.actions;

export default reconnectionNormalPaginatedSlice.reducer;
